import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCents } from '../lib/money';

// Item 2 (Dan, 2026-09-21): live, date-range-filterable sales-credit view
// so payroll can pull exactly what was sold, by which rep, in a given pay
// period. Defaults to "this month so far" on load since that's the most
// common thing an admin would check without having to pick dates first.
function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function defaultRange() {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return { startDate: isoDate(firstOfMonth), endDate: isoDate(now) };
}

const PRESETS = [
  {
    label: 'This month',
    range: () => defaultRange(),
  },
  {
    label: 'Last 30 days',
    range: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { startDate: isoDate(start), endDate: isoDate(now) };
    },
  },
  {
    label: 'Last month',
    range: () => {
      const now = new Date();
      const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastOfPrevMonth = new Date(firstOfThisMonth - 1);
      const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
      return { startDate: isoDate(firstOfPrevMonth), endDate: isoDate(lastOfPrevMonth) };
    },
  },
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Item 2 follow-up (Dan, 2026-09-21): "needs to be expandable and show the
// specific customers that were included in the count." Every rep row (and
// the unattributed line) can expand into this order-level table -- the
// backend already sends the full order list per rep/unattributed group
// alongside the totals, built from the same query, so there's no separate
// fetch and nothing to go out of sync.
function OrdersTable({ orders }) {
  if (orders.length === 0) {
    return <p style={{ margin: '8px 0 0', color: 'var(--color-text-muted)' }}>No orders in this window.</p>;
  }
  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Customer</th>
          <th>Date</th>
          <th>Plan</th>
          <th>Initial fee</th>
          <th>Monthly value</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id}>
            <td>
              {o.customerName || '(no name on file)'}
              {o.customerEmail && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{o.customerEmail}</div>}
            </td>
            <td>{formatDate(o.createdAt)}</td>
            <td>{o.planTier || '—'}</td>
            <td>{formatCents(o.initialFeeCents)}</td>
            <td>{formatCents(o.monthlyPriceCents)}</td>
            <td>
              {o.status}
              {o.commissionFlagged && (
                <div style={{ fontSize: 12, color: 'var(--color-warning, #a66a00)' }}>Flagged for review</div>
              )}
            </td>
            <td>
              <Link to={`/orders/${o.id}`}>View</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SalesCreditReport() {
  const [range, setRange] = useState(defaultRange());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRepIds, setExpandedRepIds] = useState(() => new Set());
  const [unattributedExpanded, setUnattributedExpanded] = useState(false);

  async function loadReport(r) {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ startDate: r.startDate, endDate: r.endDate });
      const result = await api.get(`/admin/sales-credit-report?${params.toString()}`);
      setReport(result);
      // A fresh report is a fresh set of rows -- start collapsed rather
      // than carrying over expansion state from the previous date range.
      setExpandedRepIds(new Set());
      setUnattributedExpanded(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport(range);
    // Only on mount -- subsequent runs are explicit via the "Run report"
    // button/presets so changing a date input doesn't refetch on every
    // keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyPreset(preset) {
    const r = preset.range();
    setRange(r);
    loadReport(r);
  }

  function submit(e) {
    e.preventDefault();
    loadReport(range);
  }

  function toggleRep(repId) {
    setExpandedRepIds((prev) => {
      const next = new Set(prev);
      if (next.has(repId)) {
        next.delete(repId);
      } else {
        next.add(repId);
      }
      return next;
    });
  }

  return (
    <div>
      <h2>Sales Credit Report</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Date range</h3>
        <form onSubmit={submit} style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label>Start date</label>
            <input
              type="date"
              value={range.startDate}
              max={range.endDate}
              onChange={(e) => setRange({ ...range, startDate: e.target.value })}
              required
            />
          </div>
          <div>
            <label>End date</label>
            <input
              type="date"
              value={range.endDate}
              min={range.startDate}
              onChange={(e) => setRange({ ...range, endDate: e.target.value })}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Running…' : 'Run report'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {PRESETS.map((p) => (
              <button key={p.label} type="button" className="btn" onClick={() => applyPreset(p)} disabled={loading}>
                {p.label}
              </button>
            ))}
          </div>
        </form>
      </div>

      {report && (
        <>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>
              Totals, {report.startDate} to {report.endDate}
            </h3>
            <p>
              {report.totals.orderCount} order{report.totals.orderCount === 1 ? '' : 's'} credited &middot;{' '}
              {formatCents(report.totals.totalInitialFeeCents)} in initial fees &middot;{' '}
              {formatCents(report.totals.totalMonthlyPriceCents)} in monthly value
            </p>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>By rep</h3>
            <p className="hint-text" style={{ marginTop: 0 }}>
              Click a rep to see the specific customers included in their count.
            </p>
            {report.reps.length === 0 ? (
              <p>No sales-credited orders in this window.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Rep</th>
                    <th>Orders</th>
                    <th>Initial fees</th>
                    <th>Monthly value</th>
                    <th>Canceled</th>
                    <th>Flagged for review</th>
                  </tr>
                </thead>
                <tbody>
                  {report.reps.map((r) => {
                    const expanded = expandedRepIds.has(r.repId);
                    return (
                      <React.Fragment key={r.repId}>
                        <tr
                          onClick={() => toggleRep(r.repId)}
                          style={{ cursor: 'pointer' }}
                          aria-expanded={expanded}
                        >
                          <td style={{ width: 20 }}>{expanded ? '▾' : '▸'}</td>
                          <td>
                            {r.repName}
                            {r.repEmail && (
                              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{r.repEmail}</div>
                            )}
                          </td>
                          <td>{r.orderCount}</td>
                          <td>{formatCents(r.totalInitialFeeCents)}</td>
                          <td>{formatCents(r.totalMonthlyPriceCents)}</td>
                          <td>{r.canceledCount}</td>
                          <td>{r.commissionFlaggedCount > 0 ? r.commissionFlaggedCount : '—'}</td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td></td>
                            <td colSpan={6} style={{ background: 'var(--color-bg-subtle, #f7f7f5)' }}>
                              <OrdersTable orders={r.orders} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 0 }}>
              "Flagged for review" is a sale canceled before the rep's commission retention gate -- informational only,
              doesn't change the totals above. Check the order's history for the reason.
            </p>
          </div>

          {report.unattributed.orderCount > 0 && (
            <div className="card">
              <h3
                style={{ marginTop: 0, cursor: 'pointer' }}
                onClick={() => setUnattributedExpanded((v) => !v)}
                aria-expanded={unattributedExpanded}
              >
                {unattributedExpanded ? '▾' : '▸'} Unattributed orders ({report.unattributed.orderCount})
              </h3>
              <p className="hint-text" style={{ marginTop: 0 }}>
                Orders in this window with no rep attached -- {formatCents(report.unattributed.totalMonthlyPriceCents)}{' '}
                monthly value, not credited to anyone above.
              </p>
              {unattributedExpanded && <OrdersTable orders={report.unattributed.orders} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
